/* eslint-disable react-hooks/rules-of-hooks */
import { createContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useColorScheme,
  useTheme,
} from '@mui/material'
import * as muiColors from '@mui/material/colors'
import { router } from '@inertiajs/react'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import { useMemoizedFn } from 'ahooks'
import ContentFor, { ContentForHost } from '@/components/ContentFor'
import useDarkModeToggle from '@/lib/hooks/useDarkModeToggle'

type DevtoolsTab = 'routerCache' | 'tanstackQuery' | 'themeColors'

export const Devtools = ({ children }: { children?: React.ReactNode }) => {
  if (!import.meta.env.DEV) {
    return <>{children}</>
  }

  const [isOpen, setIsOpen] = useState(false)
  const [tab, setTab] = useState<DevtoolsTab>('routerCache')

  return (
    <DevtoolsContext value={setIsOpen}>
      {children}
      {isOpen && <DevtoolsModal tab={tab} onTabChange={setTab} onClose={() => setIsOpen(false)} />}
    </DevtoolsContext>
  )
}

export const DevtoolsContext = createContext<
  React.Dispatch<React.SetStateAction<boolean>> | undefined
>(undefined)

const DevtoolsModal = ({
  tab,
  onTabChange,
  onClose,
}: {
  tab: DevtoolsTab
  onTabChange: (tab: DevtoolsTab) => void
  onClose: () => void
}) => {
  return (
    <Dialog open onClose={onClose} scroll='paper' fullWidth maxWidth='lg'>
      <Stack useFlexGap sx={{ height: '70vh' }}>
        <Box sx={{ px: 3, pt: 2 }}>
          <Tabs value={tab} onChange={(_, value) => onTabChange(value)}>
            <Tab label='Router cache' value='routerCache' />
            <Tab label='Tanstack Query' value='tanstackQuery' />
            <Tab label='Theme colors' value='themeColors' />
          </Tabs>
        </Box>
        <DialogContent dividers>
          {tab === 'routerCache' && <RouterCacheDevtools />}
          {tab === 'tanstackQuery' && (
            <ReactQueryDevtoolsPanel style={{ height: '100%' }} onClose={onClose} />
          )}
          {tab === 'themeColors' && <ThemeColorsDevtools />}
        </DialogContent>
        <DialogActions>
          <ContentForHost id='devtools-modal-actions' sx={{ mr: 'auto' }} />
          <Button onClick={onClose} size='large'>
            Close
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  )
}

const RouterCacheDevtools = () => {
  const codeElementRef = useRef<HTMLElement>(null)

  const allRouterCachedGet = useMemoizedFn(() => {
    const prefetchedRequests = (router as any).temp_prefetchedRequests
    if (!prefetchedRequests) {
      return 'prefetchedRequests not found'
    }

    return {
      currentUseId: prefetchedRequests.currentUseId,
      urls: prefetchedRequests.cached.map((v: any) => {
        return {
          url: (v.params.url as URL).href.slice(window.location.origin.length),
          cacheFor: (() => {
            const time = (v.staleTimestamp - v.timestamp) / 1000
            if (time >= 60) {
              return `${Math.round(time / 60)}m`
            } else {
              return `${Math.round(time)}s`
            }
          })(),
          params: (() => {
            const params = {
              data: v.params.data,
              method: v.params.method,
              headers: v.params.headers,
              preserveScroll: v.params.preserveScroll,
              preserveState: v.params.preserveState,
            }

            if (Object.keys(params.data).length === 0) {
              delete params.data
            }

            if (params.method === 'get') {
              delete params.method
            }

            if (
              Object.keys(params.headers).length === 0 ||
              (Object.keys(params.headers).length === 1 && params.headers['Purpose'] === 'prefetch')
            ) {
              delete params.headers
            }

            if (params.preserveScroll === false) {
              delete params.preserveScroll
            }

            if (params.preserveState === false) {
              delete params.preserveState
            }

            return Object.keys(params).length === 0 ? undefined : params
          })(),
          fetchedOn: new Date(v.timestamp).toLocaleString(),
          staleOn: new Date(v.staleTimestamp).toLocaleString(),
        }
      }),
    }
  })

  useEffect(() => {
    const codeElement = codeElementRef.current
    if (!codeElement) {
      return
    }

    const intervalId = setInterval(() => {
      const json = JSON.stringify(allRouterCachedGet(), null, 2)

      if (codeElement.textContent !== json) {
        codeElement.textContent = json
      }
    }, 500)

    return () => clearInterval(intervalId)
  }, [allRouterCachedGet])

  return (
    <>
      <ContentFor for='devtools-modal-actions'>
        <Button onClick={() => router.flushAll()} variant='outlined'>
          Clear cache
        </Button>
      </ContentFor>
      <Box
        component='pre'
        onClick={() => {
          console.log({ prefetchedRequests: (router as any).temp_prefetchedRequests })
        }}
        sx={{ cursor: 'pointer' }}
      >
        <Box component='code' ref={codeElementRef}>
          {JSON.stringify(allRouterCachedGet(), null, 2)}
        </Box>
      </Box>
    </>
  )
}

const ThemeColorsDevtools = () => {
  const theme = useTheme()
  const { mode } = useColorScheme()

  const { toggle, renderIcons } = useDarkModeToggle({
    renderLightModeIcon(props: any) {
      return <Button {...props}>Toggle to Dark Mode</Button>
    },
    renderDarkModeIcon(props: any) {
      return <Button {...props}>Toggle to Light Mode</Button>
    },
  })

  const allColors = (() => {
    const colors = [] as any[]

    const processColors = (obj: any, path: string[] = []) => {
      for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'string') {
          colors.push({ color: value, path: [...path, key] })
        } else {
          processColors(value, [...path, key])
        }
      }
    }

    // @ts-ignore
    processColors(theme.colorSchemes[mode].palette.oee)
    return colors as { color: string; path: string[] }[]
  })()

  const muiColorsByValue = Object.fromEntries(
    Object.entries(muiColors).flatMap(([colorName, palette]) =>
      Object.entries(palette).map(([key, color]) => [
        color,
        { color, path: [colorName, key].join('.') },
      ]),
    ),
  )

  return (
    <>
      <ContentFor for='devtools-modal-actions'>{renderIcons({ onClick: toggle })}</ContentFor>
      <Grid container columns={5} spacing={2}>
        {allColors
          .slice()
          .sort((a, b) => (a.path.join('.') > b.path.join('.') ? 1 : -1))
          .map(({ color, path }) => {
            const muiColor = muiColorsByValue[color]
            const existsInMui = !!muiColor

            return (
              <Grid
                key={path.join('.')}
                size={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={path.join('.')}
              >
                <Box
                  sx={{
                    width: '100px',
                    height: '100px',
                    backgroundColor: color,
                    border: '1px solid #00000020',
                    borderRadius: '4px',
                  }}
                />
                <Typography variant='body2'>{path.join('.')}</Typography>
                <Typography variant='body2' display='block'>
                  {color.toUpperCase()}
                </Typography>
                <Typography variant='caption' display='block'>
                  {existsInMui ? `(MUI: ${muiColor.path})` : '\u00A0'}
                </Typography>
              </Grid>
            )
          })}
      </Grid>
    </>
  )
}
